"use client";

import React, {
  useCallback,
  useContext,
  useEffect,
  useState,
  useRef,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { useRouter } from "next/navigation";
import { Icon } from "@iconify/react";

import {
  shouldLastPlayStatusChange,
  videoProgress,
  videoStart,
  viewsCount,
} from "../helpers";
import Player, { PlayerAttributes } from "@/utilities/Player";
import useVideoMetadata from "@/hooks/useVideoMetadata";
import SocketContext from "@/socket/Context";
import services from "@/store/services";
import Components from "@/components";
import sk from "@/skeletons";
import utils from "@/utils";
import "../player-side.scss";

function PlayerSection(props) {
  const { playStatus, isEnrolled, playrate, videoInfo, isStarted, courseInfo } =
    useSelector((state) => state.video);
  const [waitTime, setWaitTime] = useState(videoInfo?.isPlayable?.time || 0);
  const { violationError } = useSelector((state) => state.modal);
  const enrollments = useSelector((state) => state.enrollment);
  const metadata = useSelector((state) => state.videoMetaData);
  const { socket } = useContext(SocketContext).SocketState;
  const points = useSelector((state) => state?.loc_points);
  const user = useSelector((state) => state?.user?.users);
  const [restrictVideo, setRestrictVideo] = useState(false);

  const playTimerRef = useRef(null);

  const {
    shouldUpdateProgress,
    getVideoMetadataById,
    updateVideoMetadata,
    addVideoMetaData,
    isviewsCount,
    setPlayerRef,
  } = useVideoMetadata({ playTimerRef });

  const dispatch = useDispatch();
  const router = useRouter();

  const calculateWaitTime = useCallback(() => {
    if (videoInfo?.isPlayable?.time) {
      let interval = null;

      if (videoInfo.isPlayable.time > 0) {
        interval = setInterval(() => {
          videoInfo.isPlayable.time -= 1;
          setWaitTime(videoInfo.isPlayable.time);
        }, 1000);
      }

      return () => clearInterval(interval);
    }
  }, [videoInfo, waitTime]);

  const fetchProgress = () => {
    dispatch(services.fetchEnrollment());
  };

  const initializePlayer = useCallback(async () => {
    if (playStatus !== "" && user.email && courseInfo) {
      let prevMeta = getVideoMetadataById(
        `${courseInfo?._id}_${videoInfo._id}`
      );
      let player = PlayerAttributes();
      let ply = player.getCurrentPlayer();
      setPlayerRef(ply);

      videoStart({
        ply,
        prevMeta,
        isStarted,
        timestamp: router?.query?.timestamp,
        makeVideoStart: () =>
          dispatch(services.updateVideoInfo({ isStarted: true })),
      });

      if (!prevMeta) {
        const data = {
          id: `${courseInfo?._id}_${videoInfo._id}`,
          initiate: Date.now(),
          currsecs: 0,
          status: playStatus === "playing",
          lastplaystatus: 0,
        };
        prevMeta = await addVideoMetaData(data);
        // console.log({ prevMeta });
      } else {
        const data = {
          ...prevMeta,
          id: `${courseInfo?._id}_${videoInfo._id}`,
          status: playStatus === "playing",
          lastplaystatus:
            prevMeta.status !== true //--> paused
              ? prevMeta.lastplaystatus
              : shouldLastPlayStatusChange(prevMeta, ply),
        };
        // console.log("if prevMeta exists: ", { data });
        let metadata = await updateVideoMetadata(
          data,
          playrate,
          (timespent) => {
            dispatch(
              services.updatePoints(points, timespent, socket, user?._id)
            );
          }
        );

        // console.log("Video Meta Data", metadata);

        await viewsCount({
          meta: metadata,
          cid: courseInfo?._id,
          vid: videoInfo,
          uid: user?._id,
          socket,
          isviewsCount,
        });

        await videoProgress({
          meta: metadata,
          cid: courseInfo?._id,
          vid: videoInfo,
          uid: user._id,
          shouldUpdateProgress,
          fetchProgress,
          isEnrolled,
          socket,
        });
      }
    }
  }, [playStatus, playrate]);

  const srcChange = useCallback(() => {
    if (props.isSrcChanged && videoInfo) {
      const ply = PlayerAttributes();
      const player = ply.getCurrentPlayer();
      setPlayerRef(player);
      player?.src({
        type: "application/x-mpegURL",
        src: `${videoInfo?.videoSrc}`,
        withCredentials: true,
      });
      if (playTimerRef.current) clearInterval(playTimerRef.current);
      player?.poster(`${videoInfo?.thumbnailImage}`);
      player?.load();
      props?.changeIsSrcChange();
    }
  }, [props.isSrcChanged]);

  const toggleVideoWarningModal = () => {
    dispatch(services.toggleVideoWarningModal());
  };

  useEffect(() => {
    return () => {
      if (playTimerRef?.current) clearInterval(playTimerRef.current);
    };
  }, []);

  const handlePreEnrollTour = useCallback(() => {
    if (
      !metadata ||
      !courseInfo?._id ||
      metadata.length < 2 ||
      !enrollments.length
    )
      return;
    //NOTE - Recent Videos from same course
    const sameCourse = metadata.filter(
      (video) => video.id.split("_")[0] === courseInfo?._id
    );
    const shouldShow = sameCourse.length > 2;
    const isEnrolledInCourse = enrollments?.some(
      (enrollment) => enrollment.courseId === courseInfo?._id
    );
    if (shouldShow && !isEnrolledInCourse)
      dispatch(services.startPreEnrollTour());
  }, [metadata.length, courseInfo, enrollments]);

  useEffect(() => {
    calculateWaitTime();
  }, [calculateWaitTime]);

  useEffect(() => {
    initializePlayer();
  }, [initializePlayer]);

  useEffect(() => {
    srcChange();
  }, [srcChange]);

  const videoList =
    courseInfo?._id === videoInfo?.courseId || videoInfo?._id
      ? [
          {
            sources: [
              {
                src: !props.loading ? `${videoInfo?.videoSrc}` : null,
                type: "application/x-mpegURL",
                withCredentials: true,
              },
            ],
            poster: !props.loading ? `${videoInfo?.thumbnailImage}` : null,
          },
        ]
      : [];

  const hotKeys = {
    default: true,
    seekStep: 10,
  };

  useEffect(() => {
    handlePreEnrollTour();
  }, [handlePreEnrollTour]);

  useEffect(() => {
    if (!courseInfo || !videoInfo) return;
    const metaId = `${courseInfo._id}_${videoInfo._id}`;
    const meta = getVideoMetadataById(`${metaId}`);
    if (!meta) return;

    const player = PlayerAttributes();
    const ply = player.getCurrentPlayer();
    setPlayerRef(ply);
  }, [courseInfo?._id, videoInfo?._id, metadata]);

  return (
    <article className="vd-player">
      <Components.Modal
        block={true}
        placement="center"
        showModal={violationError}
        className={"dialog-modal"}
        setShowModal={toggleVideoWarningModal}
      >
        <Components.ErrorModal closeHandler={() => toggleVideoWarningModal()} />
      </Components.Modal>

      {/* check if video is playable */}
      {!props.loading && videoInfo?.videoSrc !== "" && videoList?.length ? (
        Object.keys(user).length === 0 && restrictVideo ? (
          <div className="ra-video-warning">
            <div>
              <Icon icon={"fxemoji:lock"} className="vps-lock" />

              <br />
              {videoInfo?.isPlayable?.status === "wait" && (
                <h1 className="vd-time-left">time exceeded</h1>
              )}
              <h3 className="vd-error-message">
                Please login to further watch the videos
              </h3>
            </div>
          </div>
        ) : (
          <Player
            id="video-player-aman"
            videoList={videoList}
            className="vjs-matrix"
            hotKeys={hotKeys}
            checkStatus={(state) =>
              dispatch(services.updateVideoInfo({ playStatus: state }))
            }
            checkPlayrate={(state) =>
              dispatch(services.updateVideoInfo({ playrate: state }))
            }
            setRestrictVideo={setRestrictVideo}
          />
        )
      ) : !props.loading && videoInfo?.videoSrc === "" ? (
        <div className="ra-video-warning">
          <div>
            {videoInfo?.isPlayable?.status === "locked" ? (
              <Icon icon={"mdi:lock-outline"} className="vps-lock" />
            ) : (
              <Icon
                icon={"mdi:timer-sand-complete"}
                className="vps-wait-icon"
              />
            )}
            <br />
            {videoInfo?.isPlayable?.status === "wait" && (
              <h1 className="vd-time-left">
                {utils.formatSeconds(videoInfo?.isPlayable?.time)}
              </h1>
            )}
            <h3 className="vd-error-message">
              {videoInfo?.isPlayable?.message}
            </h3>
          </div>
        </div>
      ) : (
        <sk.SkeletonElement type="sk-video-player" />
      )}
    </article>
  );
}

export default React.memo(PlayerSection);
